import { Capacitor } from '@capacitor/core';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth } from '../config/firebase';
import { createLogger } from './logger';

const logger = createLogger('FirebaseOAuth');

/**
 * Google OAuth using Firebase Auth
 * Uses native plugin on iOS/Android, popup on web
 */
export const signInWithGoogle = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Mobile: Use native Capacitor Firebase Auth plugin
      logger.log('📱 Starting native Google sign-in...');
      const result = await FirebaseAuthentication.signInWithGoogle();
      logger.log('📱 Native Google result:', result);
      
      if (result.user) {
        const user = result.user;
        const fullName = user.displayName || '';
        const nameParts = fullName.split(' ');
        
        return {
          email: user.email,
          given_name: nameParts[0] || '',
          family_name: nameParts.slice(1).join(' ') || '',
          picture: user.photoUrl,
          sub: user.uid
        };
      }
      throw new Error('No user returned from Google Sign-In');
    } else {
      // Web: Use popup flow
      logger.log('🌐 Starting Google sign-in with popup (web)...');
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({ prompt: 'select_account' });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      return {
        email: user.email,
        given_name: user.displayName?.split(' ')[0] || '',
        family_name: user.displayName?.split(' ').slice(1).join(' ') || '',
        picture: user.photoURL,
        sub: user.uid
      };
    }
  } catch (error) {
    logger.error('Google sign-in failed:', error);
    throw error;
  }
};

/**
 * Apple OAuth using Firebase Auth  
 * Uses native plugin on iOS/Android, popup on web
 */
export const signInWithApple = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Mobile: Use native Capacitor Firebase Auth plugin
      logger.log('🍎 Starting native Apple sign-in...');
      const result = await FirebaseAuthentication.signInWithApple();
      logger.log('🍎 Native Apple result:', result);
      
      if (result.user) {
        const user = result.user;
        const fullName = user.displayName || '';
        const nameParts = fullName.split(' ');
        
        return {
          email: user.email,
          given_name: nameParts[0] || '',
          family_name: nameParts.slice(1).join(' ') || '',
          picture: user.photoUrl,
          sub: user.uid
        };
      }
      throw new Error('No user returned from Apple Sign-In');
    } else {
      // Web: Use popup flow
      logger.log('🍎 Starting Apple sign-in with popup (web)...');
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const fullName = user.displayName || '';
      const nameParts = fullName.split(' ');
      
      return {
        email: user.email,
        given_name: nameParts[0] || '',
        family_name: nameParts.slice(1).join(' ') || '',
        picture: user.photoURL,
        sub: user.uid
      };
    }
  } catch (error) {
    logger.error('Apple sign-in failed:', error);
    throw error;
  }
};

/**
 * Check for redirect result - kept for backward compatibility
 */
export const checkOAuthRedirectResult = async () => {
  return null;
};

export const checkGoogleRedirectResult = checkOAuthRedirectResult;

/**
 * Sign out from Firebase
 */
export const signOutFromFirebase = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signOut();
    }
    await auth.signOut();
    logger.log('✅ Signed out from Firebase');
  } catch (error) {
    logger.error('Sign out failed:', error);
  }
};
